interface Message {
  id: number;
  role: string;
  content: string;
}

interface RbChatMessageListProps {
  messages: Message[];
  streamingContent: string;
}

const CONTACT_LINKS_MARKER = '[[CONTACT_LINKS]]';

function parseContactLinks(content: string): { rbUrl: string | null; srUrl: string | null; rest: string } | null {
  if (!content.startsWith(CONTACT_LINKS_MARKER)) return null;
  const remainder = content.slice(CONTACT_LINKS_MARKER.length);
  const newlineIdx = remainder.indexOf('\n\n');
  const jsonPart = newlineIdx === -1 ? remainder : remainder.slice(0, newlineIdx);
  const rest = newlineIdx === -1 ? '' : remainder.slice(newlineIdx + 2);
  try {
    const parsed = JSON.parse(jsonPart);
    return { rbUrl: parsed.rbUrl ?? null, srUrl: parsed.srUrl ?? null, rest };
  } catch {
    return null;
  }
}

// Defense-in-depth: these URLs originate from an [[CONTACT_LINKS]] marker parsed out of
// LLM-generated message content. The server (getCandidateContactLink in org-chat-tools.ts)
// currently only ever produces https:// URLs, but nothing at this layer enforces that, and
// message content flows through an LLM that could be influenced by prompt injection (e.g.
// text embedded in a resume or job description). Never render an href without validating
// its scheme first.
function isSafeHttpsUrl(url: string): boolean {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

function ContactLinkCard({ rbUrl, srUrl }: { rbUrl: string | null; srUrl: string | null }) {
  if (!rbUrl && !srUrl) {
    return (
      <div className="rounded-lg border border-gray-700 bg-gray-800 px-4 py-3 text-sm text-gray-300">
        No contact info on file for this candidate.
      </div>
    );
  }
  const rbSafe = rbUrl && isSafeHttpsUrl(rbUrl);
  const srSafe = srUrl && isSafeHttpsUrl(srUrl);
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-gray-700 bg-gray-800 px-4 py-3">
      {rbUrl && rbSafe && (
        <a href={rbUrl} target="_blank" rel="noopener noreferrer" className="text-sky-400 underline">
          View in Recruiter Buddy
        </a>
      )}
      {rbUrl && !rbSafe && (
        <span className="text-sm text-gray-400">Recruiter Buddy link could not be shown safely.</span>
      )}
      {srUrl && srSafe && (
        <a href={srUrl} target="_blank" rel="noopener noreferrer" className="text-sky-400 underline">
          View in SmartRecruiters
        </a>
      )}
      {srUrl && !srSafe && (
        <span className="text-sm text-gray-400">SmartRecruiters link could not be shown safely.</span>
      )}
    </div>
  );
}

export function RbChatMessageList({ messages, streamingContent }: RbChatMessageListProps) {
  return (
    <div className="flex flex-col gap-4 p-4">
      {messages.map((message) => {
        const contactLinks = message.role === 'assistant' ? parseContactLinks(message.content) : null;
        return (
          <div key={message.id} className={message.role === 'user' ? 'self-end' : 'self-start'}>
            {contactLinks ? (
              <>
                <ContactLinkCard rbUrl={contactLinks.rbUrl} srUrl={contactLinks.srUrl} />
                {contactLinks.rest && <p className="mt-2 text-sm text-gray-100">{contactLinks.rest}</p>}
              </>
            ) : (
              <p className="text-sm text-gray-100">{message.content}</p>
            )}
          </div>
        );
      })}
      {streamingContent && <p className="self-start text-sm text-gray-100">{streamingContent}</p>}
    </div>
  );
}
